import { useState, useCallback, useEffect } from 'react'
import { formatCurrency, formatDate, formatQuarter } from '../utils/helpers'
import apiClient from '../services/api'

/**
 * Simplified API hook for making API calls with basic state management
 * Replaces the complex useApi hook with essential functionality only
 * Follows the rule: Always prefer simple solutions
 */
export const useSimpleApi = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  /**
   * Execute API call with basic state management
   * @param {Function} apiCall - Function that returns a Promise
   * @returns {Promise} The result of the API call
   */
  const execute = useCallback(async (apiCall) => {
    setLoading(true)
    setError(null)
    
    try {
      const result = await apiCall()
      setData(result)
      return result
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Reset all states to initial values
   */
  const reset = useCallback(() => {
    setLoading(false)
    setError(null)
    setData(null)
  }, [])

  return { 
    loading, 
    error, 
    data, 
    execute, 
    reset 
  }
}

/**
 * Simplified data fetching hook that replaces the complex useDataFetching system
 * Uses parallel fetching by default and provides a single interface for all data types
 * Follows the rule: Always prefer simple solutions, avoid duplication of code
 */
export const useData = (type, startupId = null, filters = {}) => {
  const api = useSimpleApi()

  /**
   * Fetch data based on type with parallel API calls for optimal performance
   * @param {Object} filters - Optional filtering parameters for server-side filtering
   */
  const fetchData = useCallback(async (customFilters = {}) => {
    if (!type) return

    try {
      let apiCall
      
      // Merge default filters with custom filters
      const activeFilters = { ...filters, ...customFilters }
      
      switch (type) {
        case 'dashboard':
          // Parallel fetching for dashboard - all calls run simultaneously
          // Using Promise.allSettled for better resilience on poor connections
          const dashboardResults = await Promise.allSettled([
            apiClient.getDashboardMetrics(),
            apiClient.getInvestments(),
            apiClient.getUpdates(),
            apiClient.getFollowOns()
          ])
          
          // Extract results with graceful fallbacks
          const dashboardMetrics = dashboardResults[0].status === 'fulfilled' ? dashboardResults[0].value : null
          const dashboardInvestments = dashboardResults[1].status === 'fulfilled' ? dashboardResults[1].value : null
          const dashboardUpdates = dashboardResults[2].status === 'fulfilled' ? dashboardResults[2].value : null
          const dashboardFollowOns = dashboardResults[3].status === 'fulfilled' ? dashboardResults[3].value : null
          
          // Log any failed requests for debugging
          dashboardResults.forEach((result, index) => {
            if (result.status === 'rejected') {
              const apiNames = ['metrics', 'investments', 'updates', 'followOns']
              console.warn(`Failed to fetch dashboard ${apiNames[index]}:`, result.reason)
            }
          })
          
          apiCall = () => Promise.resolve({ 
            metrics: dashboardMetrics, 
            investments: dashboardInvestments, 
            updates: dashboardUpdates, 
            followOns: dashboardFollowOns 
          })
          break
          
        case 'portfolio':
          // Parallel fetching for portfolio with server-side filtering
          // Using Promise.allSettled for better resilience on poor connections
          const portfolioResults = await Promise.allSettled([
            apiClient.getStartups(activeFilters),
            apiClient.getFollowOns()
          ])
          
          // Extract results with graceful fallbacks
          const portfolioCompanies = portfolioResults[0].status === 'fulfilled' ? portfolioResults[0].value : null
          const portfolioFollowOns = portfolioResults[1].status === 'fulfilled' ? portfolioResults[1].value : null
          
          // Log any failed requests for debugging
          portfolioResults.forEach((result, index) => {
            if (result.status === 'rejected') {
              const apiNames = ['companies', 'followOns']
              console.warn(`Failed to fetch portfolio ${apiNames[index]}:`, result.reason)
            }
          })
          
          apiCall = () => Promise.resolve({ 
            companies: portfolioCompanies, 
            followOns: portfolioFollowOns 
          })
          break
          
        case 'company':
          if (!startupId) return
          // Parallel fetching for company details - all calls run simultaneously
          // Using Promise.allSettled for better resilience on poor connections
          const results = await Promise.allSettled([
            apiClient.getStartup(startupId),
            apiClient.getCompanyMetrics(startupId),
            apiClient.getInvestmentsByStartup(startupId),
            apiClient.getStartupUpdates(startupId),
            apiClient.getStartupFollowOns(startupId)
          ])
          
          // Extract results with graceful fallbacks for failed requests
          const companyDetails = results[0].status === 'fulfilled' ? results[0].value : null
          const companyMetrics = results[1].status === 'fulfilled' ? results[1].value : null
          const companyInvestments = results[2].status === 'fulfilled' ? results[2].value : null
          const companyUpdates = results[3].status === 'fulfilled' ? results[3].value : null
          const companyFollowOns = results[4].status === 'fulfilled' ? results[4].value : null
          
          // Log any failed requests for debugging (but don't break the UI)
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              const apiNames = ['startup', 'metrics', 'investments', 'updates', 'followOns']
              console.warn(`Failed to fetch ${apiNames[index]}:`, result.reason)
            }
          })
          
          apiCall = () => Promise.resolve({ 
            company: companyDetails, 
            metrics: companyMetrics, 
            investments: companyInvestments, 
            updates: companyUpdates,
            followOns: companyFollowOns
          })
          break
          
        default:
          return
      }
      
      await api.execute(apiCall)
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error)
    }
  }, [type, startupId])

  // Auto-fetch data on mount or when dependencies change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Memoize refetchWithFilters to prevent unnecessary re-renders
  const refetchWithFilters = useCallback((newFilters) => {
    return fetchData(newFilters)
  }, [fetchData])

  return {
    ...api,
    refetch: fetchData,
    refetchWithFilters
  }
}

/**
 * Specialized hook for dashboard metrics with default structure
 * Eliminates the need to duplicate metric configuration in components
 */
export const useDashboardMetrics = () => {
  const api = useSimpleApi()

  /**
   * Transform API data to match UI structure
   * @param {Object} metricsData - API response data
   * @param {boolean} loading - Loading state
   * @param {Array} iconComponents - Array of icon components [DollarSign, Building2, TrendingUp]
   * @returns {Array} Transformed metrics array
   */
  const getMetrics = useCallback((metricsData, loading, iconComponents) => {
    // Default metrics structure - icons and colors are hardcoded as requested
    const defaultMetrics = [
      {
        title: 'Total Invested',
        icon: iconComponents?.[0] || 'DollarSign',
        color: 'bg-blue-500'
      },
      {
        title: 'Portfolio Companies',
        icon: iconComponents?.[1] || 'Building2',
        color: 'bg-green-500'
      },
      {
        title: 'Total Deals',
        icon: iconComponents?.[2] || 'TrendingUp',
        color: 'bg-purple-500'
      }
    ]

    if (!metricsData) {
      return defaultMetrics.map(metric => ({
        ...metric,
        value: loading ? 'Loading...' : 'N/A'
      }))
    }

    return defaultMetrics.map(metric => ({
      ...metric,
      value: metric.title === 'Total Invested' 
        ? formatCurrency(metricsData['total-invested'] || 0)
        : metric.title === 'Portfolio Companies'
        ? (metricsData['portfolio-companies'] || 0).toString()
        : (metricsData['total-deals'] || 0).toString()
    }))
  }, [])

  return {
    ...api,
    getMetrics,
  }
}

/**
 * Transform company detail metrics to AccountOverview format
 * Provides three metrics: Total Invested, Latest Investment, Total Deals
 * Icons and colors mirror dashboard for UI continuity
 * @param {Object} metricsData - API response for a single company
 * @param {boolean} loading - Loading state
 * @param {Array} iconComponents - [DollarSign, Calendar, TrendingUp]
 * @returns {Array} Metrics array consumable by AccountOverview
 */
export const getCompanyMetrics = (metricsData, loading, iconComponents) => {
  // Default definitions with consistent icons/colors
  const defaultMetrics = [
    { title: 'Total Invested', icon: iconComponents?.[0] || 'DollarSign', color: 'bg-blue-500' },
    { title: 'Latest Investment', icon: iconComponents?.[1] || 'Calendar', color: 'bg-green-500' },
    { title: 'Total Deals', icon: iconComponents?.[2] || 'TrendingUp', color: 'bg-purple-500' }
  ]

  // If missing data, surface loading/N/A for better UX during fetch/errors
  if (!metricsData) {
    return defaultMetrics.map(metric => ({
      ...metric,
      value: loading ? 'Loading...' : 'N/A'
    }))
  }

  // Map API fields to display strings with formatting helpers
  return defaultMetrics.map(metric => ({
    ...metric,
    value: metric.title === 'Total Invested'
      ? formatCurrency(metricsData?.investment || 0)
      : metric.title === 'Latest Investment'
      ? formatDate(metricsData?.['investment-latest-date'])
      : String(metricsData?.['investment-count'] || 0)
  }))
}

/**
 * Transform investment data to timeline format
 * @param {Object} apiData - Raw API response data
 * @returns {Array} Timeline-formatted investment data
 */
export const transformToTimeline = (apiData) => {
  if (!apiData?.results) {
    return []
  }

  // Transform each investment to timeline format
  const timelineData = apiData.results.map(investment => ({
    id: investment.id,
    company: investment.StartupName,
    startupId: investment.StartupCorporateEntityID,
    investmentDate: investment.StartupLegalInvDate,
    investmentAmount: investment.InvestmentAmount,
    totalAmountInvested: investment.totalAmountInvested,
    status: investment.Status,
    series: investment.Series,
    instrument: investment.Instrument,
    spv: investment.SPV,
    secondarySpv: investment.SecondarySPV,
    investmentSummit: investment.InvestmentSummit,
    investmentSummitQuarter: formatQuarter(investment.InvestmentSummitQuarter),
    subscriptionDocumentsUrl: investment.SubscriptionDocumentsFolderURL,
    documents: investment.documents
  }))

  // Sort by investment date (most recent first)
  return timelineData.sort((a, b) => {
    const dateA = new Date(a.investmentDate)
    const dateB = new Date(b.investmentDate)
    return dateB - dateA
  })
}

/**
 * Group investments by InvestmentSummitQuarter for timeline display
 * @param {Array} timelineData - Timeline-formatted investment data
 * @returns {Object} Investments grouped by quarter
 */
export const groupByQuarter = (timelineData) => {
  const grouped = {}
  
  timelineData.forEach(investment => {
    const quarter = investment.investmentSummitQuarter
    
    if (!grouped[quarter]) {
      grouped[quarter] = {
        quarter: quarter,
        displayQuarter: quarter, // Keep original quarter format (e.g., "Q1 2024")
        investments: []
      }
    }
    
    grouped[quarter].investments.push(investment)
  })

  // Convert to array and sort by quarter (most recent first)
  // Parse quarter string to sort chronologically
  return Object.values(grouped).sort((a, b) => {
    // Extract year and quarter from formatted strings like "Q1 2024", "Q2 2024"
    const parseQuarter = (quarterStr) => {
      const match = quarterStr.match(/Q(\d)\s+(\d{4})/)
      if (match) {
        const [, quarter, year] = match
        return parseInt(year) * 4 + parseInt(quarter)
      }
      return 0
    }
    
    return parseQuarter(b.quarter) - parseQuarter(a.quarter)
  })
}

/**
 * Transform portfolio companies API data to match UI structure
 * @param {Object} companiesData - Raw companies data from API
 * @returns {Array} Transformed companies array
 */
export const transformPortfolioCompanies = (companiesData) => {
  const items = companiesData?.results || []
  
  return items.map((startup) => ({
    id: startup.id,
    company: startup.name,
    hqProvince: startup.hqProvince || 'Unknown',
    logo: startup.logo || null,
    industry: startup.industry || 'Unknown',
    status: 'Active', // This would be determined by actual investment data
    statusColor: 'bg-blue-500',
    totalInvested: formatCurrency(startup.totalInvested || '0'),
    deals: startup.deals || 0,
  }))
}
